import React from 'react';
import styled from 'styled-components';
import MarkButton, { MarkStyles } from '../../../Buttons/Mark';
import BlockButton from '../../../Buttons/Block';
import BaseButton from '../../../Buttons/components/Base';
import OptionsButton from '../../../Buttons/components/Options';
import {
  getAlignmentOptions,
  getFontSizeOptions,
} from '../../../Buttons/components/Options/buttonOptions';
import { toggleIndent } from '~/components/organism/PluginsEditor/commands/modify/block';
import { tooltipText } from '../../../Buttons/text';
import ColorButton from '../../../Buttons/Color';
import ELEMENTS from '../../../elements/elementsEnum';
import InsertImageButton from '../../../Buttons/InsertImage';
import InsertVariableButton from '../../../Buttons/InsertVariable';
import { HistoryEditor } from 'slate-history';
import EditHtmlButton from '../../../Buttons/EditHtml';
import Button from '~/components/atom/Button';
import TEST_ID from './index.testid';
import InsertLinkButton from '../../../Buttons/InsertLink';
import InsertHtmlButton from '../../../Buttons/InsertHtml';
import getTypographyOptions from '../../../Buttons/components/Options/buttonOptions/typography';
import type { DHEditor } from '~/components/organism/PluginsEditor/types';
import InsertDeepLinkButton from '../../../Buttons/InsertDeepLink';

type ToolbarButton = { type: 'button'; name: ButtonName };
type ToolbarDivider = { type: 'divider' };
export type ToolbarComponent = ToolbarButton | ToolbarDivider;

export type ButtonName =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'ordered-list'
  | 'unordered-list'
  | 'indent'
  | 'outdent'
  | 'align'
  | 'color'
  | 'background-color'
  | 'font-size'
  | 'font-family'
  | 'image'
  | 'variable'
  | 'link'
  | 'blockquote'
  | 'insert-html'
  | 'undo'
  | 'redo'
  | 'full-screen'
  | 'debug'
  | 'typography'
  | 'deep-link'
  | '|';

const text = {
  exitFullscreen: 'Volledig scherm verlaten',
  fontFamilyExplanation:
    'Ga naar Huisstijl in het menu om het lettertype te wijzigen',
};

export type Props = {
  editor: DHEditor;

  /** Custom elements in the editor */
  customElements: Array<ELEMENTS>;

  /** Is the editor in full screen mode */
  isFullScreen: boolean;

  /** Buttons to show */
  buttons: Array<ToolbarComponent>;

  /** Used in full-screen button */
  onChangeFullScreen: (value: boolean) => void;
};

const Buttons: React.FCC<Props> = ({
  buttons = [],
  editor,
  customElements,
  isFullScreen,
  onChangeFullScreen,
}) => (
  <>
    {buttons.map((button, index) => {
      if (button.type === 'divider') {
        return <CustomDivider key={`divider-${index}`} />;
      }

      const commonProps: { key: string; editor: DHEditor } = {
        key: button.name,
        editor,
      };

      switch (button.name) {
        case 'bold':
          return (
            <MarkButton
              {...commonProps}
              icon="bold"
              format={MarkStyles.STRONG}
              dataTestId={TEST_ID.BOLD_BUTTON}
            />
          );

        case 'italic':
          return (
            <MarkButton
              {...commonProps}
              icon="italic"
              format={MarkStyles.EM}
              dataTestId={TEST_ID.ITALIC_BUTTON}
            />
          );

        case 'underline':
          return (
            <MarkButton
              {...commonProps}
              icon="underline"
              format={MarkStyles.U}
              dataTestId={TEST_ID.UNDERLINE_BUTTON}
            />
          );

        case 'ordered-list':
          return (
            <BlockButton
              {...commonProps}
              icon="numbered-list"
              format={ELEMENTS.OL}
              dataTestId={TEST_ID.OL_BUTTON}
            />
          );

        case 'unordered-list':
          return (
            <BlockButton
              {...commonProps}
              icon="list"
              format={ELEMENTS.UL}
              dataTestId={TEST_ID.UL_BUTTON}
            />
          );

        case 'typography':
          const typographyOptions = getTypographyOptions(editor);

          return (
            <OptionsButton
              {...commonProps}
              icon="type"
              options={typographyOptions}
              tooltipMessage={tooltipText.typography}
              dataTestId={TEST_ID.TYPOGRAPHY_BUTTON}
            />
          );

        case 'indent':
          return (
            <BaseButton
              key={button.name}
              icon="indent"
              onClick={() => toggleIndent(editor, true)}
              tooltipMessage={tooltipText.indent}
              dataTestId={TEST_ID.INDENT_BUTTON}
            />
          );

        case 'outdent':
          return (
            <BaseButton
              key={button.name}
              icon="outdent"
              onClick={() => toggleIndent(editor, false)}
              tooltipMessage={tooltipText.outdent}
              dataTestId={TEST_ID.OUTDENT_BUTTON}
            />
          );

        case 'align':
          const alignmentOptions = getAlignmentOptions(editor);

          return (
            <OptionsButton
              {...commonProps}
              icon="align-left"
              options={alignmentOptions}
              tooltipMessage={tooltipText.alignment}
              dataTestId={TEST_ID.ALIGN_BUTTON}
              persistFocus
            />
          );
        case 'color':
          return (
            <ColorButton
              {...commonProps}
              format="color"
              icon="text-color"
              dataTestId={TEST_ID.COLOR_BUTTON}
            />
          );

        case 'background-color':
          return (
            <ColorButton
              {...commonProps}
              format="backgroundColor"
              icon="text-background"
              dataTestId={TEST_ID.BACKGROUND_COLOR_BUTTON}
            />
          );

        case 'font-size':
          const fontSizeOptions = getFontSizeOptions(editor);

          return (
            <OptionsButton
              {...commonProps}
              icon="font-size"
              options={fontSizeOptions}
              tooltipMessage={tooltipText.fontSize}
              dataTestId={TEST_ID.FONT_SIZE_BUTTON}
              persistFocus
            />
          );

        case 'font-family':
          return (
            <BaseButton
              key={button.name}
              icon="type"
              onClick={() => {}}
              tooltipMessage={text.fontFamilyExplanation}
              disabled
            />
          );

        case 'image':
          if (!customElements.includes(ELEMENTS.DH_IMAGE)) return null;

          return (
            <InsertImageButton
              {...commonProps}
              dataTestId={TEST_ID.INSERT_IMAGE_BUTTON}
            />
          );

        case 'link':
          return (
            <InsertLinkButton
              {...commonProps}
              dataTestId={TEST_ID.INSERT_LINK_BUTTON}
            />
          );

        case 'blockquote':
          return (
            <BlockButton
              {...commonProps}
              icon="quote"
              format={ELEMENTS.BLOCKQUOTE}
              dataTestId={TEST_ID.BLOCKQUOTE_BUTTON}
            />
          );

        case 'variable':
          if (!customElements.includes(ELEMENTS.VARIABLE)) return null;
          return (
            <InsertVariableButton
              key={button.name}
              dataTestId={TEST_ID.INSERT_VARIABLE_BUTTON}
            />
          );

        case 'deep-link':
          if (!customElements.includes(ELEMENTS.DEEP_LINK)) return null;
          return (
            <InsertDeepLinkButton
              key={button.name}
              dataTestId={TEST_ID.INSERT_VARIABLE_BUTTON}
            />
          );

        case 'insert-html':
          return (
            <InsertHtmlButton
              {...commonProps}
              dataTestId={TEST_ID.INSERT_HTML_BUTTON}
              customElements={customElements}
            />
          );

        case 'undo':
          return (
            <BaseButton
              key={button.name}
              icon="undo"
              onClick={() => HistoryEditor.undo(editor)}
              disabled={editor.history.undos.length === 0}
              tooltipMessage={tooltipText.undo}
              dataTestId={TEST_ID.UNDO_BUTTON}
            />
          );

        case 'redo':
          return (
            <BaseButton
              key={button.name}
              icon="redo"
              onClick={() => HistoryEditor.redo(editor)}
              disabled={editor.history.redos.length === 0}
              tooltipMessage={tooltipText.redo}
              dataTestId={TEST_ID.REDO_BUTTON}
            />
          );

        case 'full-screen':
          if (isFullScreen) {
            return (
              <Button
                key={button.name}
                label={text.exitFullscreen}
                onClick={() => onChangeFullScreen(false)}
                ghost
                margin={[null, 's']}
              />
            );
          } else {
            return (
              <BaseButton
                key={button.name}
                icon="maximize"
                onClick={() => onChangeFullScreen(true)}
                tooltipMessage={tooltipText.fullscreen}
                dataTestId={TEST_ID.REDO_BUTTON}
              />
            );
          }

        case 'debug':
          return (
            <EditHtmlButton {...commonProps} customElements={customElements} />
          );

        default:
          return null;
      }
    })}
  </>
);

const CustomDivider = styled.div<{}>`
  width: 1px;
  height: 2.2em;
  margin: 0 5px;
  align-self: center;
  background-color: ${({ theme }) => theme.color('tertiary', 'light')};
`;

export default Buttons;
